import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  LogIn,
  Mail,
  Eye,
  EyeOff,
  ArrowLeft,
} from 'lucide-react';

import { BASE_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { loginCpfSchema, LoginCpfFormData } from '../../utils/validations';
import {
  fieldClassName,
  primaryButtonClassName,
  passwordToggleClassName,
  linkClassName,
} from './authStyles';

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToRegister?: () => void;
}

//Função formatCPF movida para fora
const formatCPF = (value: string) => {
  const cleaned = value.replace(/\D/g, '');
  const part1 = cleaned.substring(0, 3);
  const part2 = cleaned.substring(3, 6);
  const part3 = cleaned.substring(6, 9);
  const part4 = cleaned.substring(9, 11);

  if (cleaned.length <= 3) return part1;
  if (cleaned.length <= 6) return `${part1}.${part2}`;
  if (cleaned.length <= 9) return `${part1}.${part2}.${part3}`;
  return `${part1}.${part2}.${part3}-${part4}`;
};

export default function LoginForm({
  onSuccess,
  onSwitchToRegister,
}: LoginFormProps): JSX.Element {
  const { signIn } = useAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [emailOrCpf, setEmailOrCpf] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LoginCpfFormData>({
    resolver: zodResolver(loginCpfSchema),
    mode: 'onChange',
  });

  const cpf = watch('cpf') || '';
  const senha = watch('senha') || '';

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const cleaned = inputValue.replace(/\D/g, '');
    const formatted = formatCPF(cleaned);
    setValue('cpf', formatted);
  };

  const onSubmit = async (data: LoginCpfFormData) => {
    setIsSubmitting(true);
    setErrorMessage('');
    setInfoMessage('');

    try {
      const cpfLimpo = data.cpf.replace(/\D/g, '');

      if (!cpfLimpo || cpfLimpo.length !== 11) {
        throw new Error('CPF deve ter 11 dígitos');
      }

      if (!data.senha || data.senha.length < 6) {
        throw new Error('Senha deve ter pelo menos 6 caracteres');
      }

      await signIn(cpfLimpo, data.senha);

      /* setInfoMessage('Login realizado com sucesso! Redirecionando...'); */

      // Redirecionamento será feito pelo Cadastro.tsx após envio do token
    } catch (error: any) {
      console.error('Erro no login:', error);

      let errorMsg = 'Erro ao fazer login. Tente novamente.';

      if (
        error.message.includes('inválido') ||
        error.message.includes('Invalid')
      ) {
        errorMsg = 'CPF ou senha incorretos';
      } else if (error.message.includes('não encontrado')) {
        errorMsg =
          'CPF ou senha não encontrado no sistema. Verifique seu CPF ou senha informado.';
      } else if (
        error.message.includes('não pertence a nenhuma empresa cadastrada')
      ) {
        errorMsg =
          'Seu CPF não está vinculado a uma empresa parceira do Clube de Benefícios. Entre em contato com o RH da sua empresa para ativar seu acesso.';
      } else if (error.message) {
        errorMsg = error.message;
      }

      setErrorMessage(errorMsg);
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailOrCpf.trim()) {
      setErrorMessage('Informe seu CPF ou e-mail.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setInfoMessage('');

    try {
      const url = `${BASE_URL}/club/RecuperarSenha`;

      let cpfOuEmail = emailOrCpf.trim();

      // Se não contém @, é CPF - remover formatação
      if (!cpfOuEmail.includes('@')) {
        cpfOuEmail = cpfOuEmail.replace(/\D/g, '');
      }

      const response = await axios.post(
        url,
        { cpf_email: cpfOuEmail },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 15000,
        },
      );

      if (response.status === 200) {
        if (response.data) {
          if (response.data.mensagem) {
            if (
              response.data.mensagem.includes('enviadas') ||
              response.data.mensagem.includes('celular') ||
              response.data.mensagem.includes('Instruções')
            ) {
              setInfoMessage(`${response.data.mensagem}`);
            } else {
              setErrorMessage(`${response.data.mensagem}`);
            }
          } else {
            setInfoMessage('Solicitação processada, verifique seu celular!');
          }
        } else {
          setInfoMessage('Solicitação enviada, verifique seu celular!');
        }
      } else {
        setErrorMessage(`Erro: Status ${response.status}`);
      }
    } catch (error: any) {
      console.error('Erro capturado:', error);

      let errorMsg = 'Erro ao enviar solicitação. Tente novamente.';

      if (error.response?.status === 404) {
        errorMsg =
          'CPF ou e-mail não encontrado. Verifique os dados informados.';
      } else if (error.response?.status === 500) {
        errorMsg =
          'Erro interno do servidor. Tente novamente em alguns minutos.';
      } else if (error.response?.data?.mensagem) {
        errorMsg = error.response.data.mensagem;
      } else if (error.code === 'ECONNABORTED') {
        errorMsg = 'Timeout na requisição. Verifique sua conexão.';
      } else if (error.code === 'ECONNREFUSED') {
        errorMsg = 'Servidor não está respondendo. Verifique se está rodando.';
      }

      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full min-w-0">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          {forgotPassword ? 'Recuperar senha' : 'Bem-vindo (a)'}
        </h1>
        {!forgotPassword && (
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-gray-600">
            <p>
              Seu clube de vantagens está aqui para oferecer o melhor para você!
            </p>
            <p>
              Já faz parte do clube? Entre com suas credenciais e continue
              aproveitando seus benefícios.
            </p>
          </div>
        )}
      </header>

      {(errorMessage || infoMessage) && (
        <div
          role={errorMessage ? 'alert' : 'status'}
          aria-live={errorMessage ? 'assertive' : 'polite'}
          className={`mb-6 flex items-start gap-3 rounded-lg border p-4 ${
            errorMessage
              ? 'border-red-200 bg-red-50'
              : 'border-green-200 bg-green-50'
          }`}
        >
          {errorMessage ? (
            <AlertCircle
              className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600"
              aria-hidden="true"
            />
          ) : (
            <CheckCircle
              className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
              aria-hidden="true"
            />
          )}
          <p
            className={`min-w-0 break-words text-sm ${
              errorMessage ? 'text-red-800' : 'text-green-800'
            }`}
          >
            {errorMessage || infoMessage}
          </p>
        </div>
      )}

      {!forgotPassword ? (
        <>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label
                htmlFor="clube-login-cpf"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                CPF <span className="text-red-500">*</span>
              </label>
              <input
                {...register('cpf')}
                id="clube-login-cpf"
                type="text"
                inputMode="numeric"
                autoComplete="username"
                placeholder="000.000.000-00"
                maxLength={14}
                value={cpf}
                onChange={e => {
                  const formatted = formatCPF(e.target.value);
                  setValue('cpf', formatted, { shouldValidate: true });
                }}
                aria-invalid={Boolean(errors.cpf)}
                aria-describedby={
                  errors.cpf ? 'clube-login-cpf-error' : undefined
                }
                className={fieldClassName}
              />
              {errors.cpf && (
                <p
                  id="clube-login-cpf-error"
                  className="mt-2 text-sm text-red-600"
                  role="alert"
                >
                  {errors.cpf.message}
                </p>
              )}
            </div>

            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <label
                  htmlFor="clube-login-senha"
                  className="text-sm font-medium text-gray-700"
                >
                  Senha <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setForgotPassword(true);
                    setErrorMessage('');
                    setInfoMessage('');
                  }}
                  className={`${linkClassName} text-sm`}
                >
                  Esqueceu a senha?
                </button>
              </div>
              <div className="relative">
                <input
                  {...register('senha')}
                  id="clube-login-senha"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Digite sua senha"
                  aria-invalid={Boolean(errors.senha)}
                  aria-describedby={
                    errors.senha ? 'clube-login-senha-error' : undefined
                  }
                  className={`${fieldClassName} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  aria-controls="clube-login-senha"
                  aria-pressed={showPassword}
                  className={passwordToggleClassName}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Eye className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
              {errors.senha && (
                <p
                  id="clube-login-senha-error"
                  className="mt-2 text-sm text-red-600"
                  role="alert"
                >
                  {errors.senha.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={
                isSubmitting || !cpf || !senha || Object.keys(errors).length > 0
              }
              className={primaryButtonClassName}
            >
              {isSubmitting ? (
                <>
                  <Loader2
                    className="h-5 w-5 animate-spin"
                    aria-hidden="true"
                  />{' '}
                  Entrando...
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" aria-hidden="true" /> Entrar
                </>
              )}
            </button>
          </form>

          {onSwitchToRegister && (
            <div className="mt-6 border-t border-gray-100 pt-6 text-center">
              <p className="text-sm text-gray-600">
                É sua primeira vez aqui?{' '}
                <button
                  type="button"
                  onClick={onSwitchToRegister}
                  className={linkClassName}
                >
                  Crie sua conta
                </button>
                .
              </p>
            </div>
          )}
        </>
      ) : (
        <form onSubmit={handleForgotPassword} className="space-y-5">
          <div>
            <label
              htmlFor="clube-recuperacao"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              CPF ou e-mail <span className="text-red-500">*</span>
            </label>
            <input
              id="clube-recuperacao"
              type="text"
              autoComplete="username"
              placeholder="Digite seu CPF ou e-mail"
              value={emailOrCpf}
              onChange={e => setEmailOrCpf(e.target.value)}
              className={fieldClassName}
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting || !emailOrCpf.trim()}
            className={primaryButtonClassName}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />{' '}
                Enviando...
              </>
            ) : (
              <>
                <Mail className="h-5 w-5 flex-shrink-0" aria-hidden="true" />{' '}
                Enviar link
              </>
            )}
          </button>
          <div className="border-t border-gray-100 pt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setForgotPassword(false);
                setErrorMessage('');
                setInfoMessage('');
                setEmailOrCpf('');
              }}
              className={`${linkClassName} inline-flex min-h-[44px] items-center justify-center gap-2 text-sm`}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Voltar ao login
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
